import Decimal from 'decimal.js';
import { SortStowageColumnEnum } from 'src/enums/stowage/sortStowageColumnEnum';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { notStowedWeightInTanks, stowedWeightInTanks } from './stowageOperation/stowageOperationUtils';
import { calculateVolume } from './stowageOperation/volumeUtils';
import { getCargoTemperature } from './stowageOperation/cargoTemperatureUtils';

const compare = (a: any, b: any, isDesc: boolean): number => {
    if (a === b) return 0;
    if (a == null && b == null) return 0;
    if (a == null) return isDesc ? 1 : -1;
    if (b == null) return isDesc ? -1 : 1;
    return isDesc ? (a > b ? -1 : 1) : a > b ? 1 : -1;
};

const compareDecimals = (a: Decimal, b: Decimal, isDesc: boolean): number => {
    if (a.eq(b)) return 0;
    return isDesc ? (a.gt(b) ? -1 : 1) : a.gt(b) ? 1 : -1;
};

function comparePortNames(portNamesA: any[], portNamesB: any[], isDesc: boolean): number {
    for (let i = 0; i < Math.min(portNamesA.length, portNamesB.length); i++) {
        const res = compare(portNamesA[i].name, portNamesB[i].name, isDesc);
        if (res) {
            return res;
        }
    }
    return 0;
}

const comparePorts = (cargoA: RotationCargo | null, cargoB: RotationCargo | null, isDesc: boolean): number => {
    if (!cargoA && !cargoB) return 0;
    if (!cargoA) return isDesc ? 1 : -1;
    if (!cargoB) return isDesc ? -1 : 1;

    if (cargoB.loadingPortNames.length > 0 && cargoA.loadingPortNames.length > 0) {
        let res = comparePortNames(cargoA.loadingPortNames, cargoB.loadingPortNames, isDesc);
        if (res) {
            return res;
        }

        if (cargoB.unloadingPortNames.length > 0 && cargoA.unloadingPortNames.length > 0) {
            res = comparePortNames(cargoA.unloadingPortNames, cargoB.unloadingPortNames, isDesc);
            if (res) {
                return res;
            }
        }

        // If all compared ports are equal, compare the lengths of the arrays
        if (cargoA.loadingPortNames.length !== cargoB.loadingPortNames.length) {
            return isDesc
                ? cargoB.loadingPortNames.length - cargoA.loadingPortNames.length
                : cargoA.loadingPortNames.length - cargoB.loadingPortNames.length;
        }

        if (cargoA.unloadingPortNames.length !== cargoB.unloadingPortNames.length) {
            return isDesc
                ? cargoB.unloadingPortNames.length - cargoA.unloadingPortNames.length
                : cargoA.unloadingPortNames.length - cargoB.unloadingPortNames.length;
        }

        return 0;
    }
    return 0;
};

const addSign = (cargoOperation: RotationCargoOperation, value: number): number => {
    return value * (cargoOperation.operationType === 'Unloading' ? -1 : 1);
};

/**
 * Sorts cargo items first by voyage number, then by cargo number
 * @param cargoA First cargo to compare
 * @param cargoB Second cargo to compare
 * @param isDesc Whether to sort in descending order
 * @returns Sort comparison result (-1, 0, or 1)
 */
export function compareCargos(cargoA: RotationCargo | null | undefined, cargoB: RotationCargo | null | undefined, isDesc: boolean): number {
    // First compare voyage numbers
    const voyageCompare = compare(cargoA?.voyageNumber, cargoB?.voyageNumber, isDesc);
    // If voyage numbers are equal, then compare cargo numbers
    return voyageCompare !== 0 ? voyageCompare : compare(cargoA?.cargoNumber, cargoB?.cargoNumber, isDesc);
}

export function onSortColumn(
    operationListSortColumn: SortStowageColumnEnum,
    isOperationListSortDesc: boolean,
    a: RotationCargoOperation,
    b: RotationCargoOperation,
    activeStowagePlan: StowagePlan | null
): number {
    switch (operationListSortColumn) {
        case SortStowageColumnEnum.NoSort:
            return compare(a.cargoOperationSequenceNumber ?? 0, b.cargoOperationSequenceNumber ?? 0, false);
        case SortStowageColumnEnum.CargoNr:
            return compareCargos(a.cargo, b.cargo, isOperationListSortDesc);
        case SortStowageColumnEnum.NominatedWeight:
            return compare(addSign(a, a.nominatedWeight ?? 0), addSign(b, b.nominatedWeight ?? 0), isOperationListSortDesc);
        case SortStowageColumnEnum.NominatedVolume:
            if (!activeStowagePlan) return 0;
            // If both have null/undefined nominated weights, they are equal
            if (a.nominatedWeight == null && b.nominatedWeight == null) return 0;
            return compareDecimals(
                calculateVolume(new Decimal(a.nominatedWeight ?? 0), a.cargo ?? null, getCargoTemperature(activeStowagePlan, a.cargo ?? null)),
                calculateVolume(new Decimal(b.nominatedWeight ?? 0), b.cargo ?? null, getCargoTemperature(activeStowagePlan, b.cargo ?? null)),
                isOperationListSortDesc
            );
        case SortStowageColumnEnum.NotStowedWeight:
            if (!activeStowagePlan) return 0;
            return compareDecimals(
                notStowedWeightInTanks(activeStowagePlan, a),
                notStowedWeightInTanks(activeStowagePlan, b),
                isOperationListSortDesc
            );
        case SortStowageColumnEnum.NotStowedVolume:
            if (!activeStowagePlan) return 0; // TODO: LOGIC-ERROR - This case uses notStowedWeightInTanks() instead of calculating actual volume. Should use calculateVolume() with notStowedWeightInTanks() result, similar to NominatedVolume case.
            return compareDecimals(
                notStowedWeightInTanks(activeStowagePlan, a),
                notStowedWeightInTanks(activeStowagePlan, b),
                isOperationListSortDesc
            );
        case SortStowageColumnEnum.StowedWeight:
            if (!activeStowagePlan) return 0;
            return compareDecimals(stowedWeightInTanks(activeStowagePlan, a), stowedWeightInTanks(activeStowagePlan, b), isOperationListSortDesc);
        case SortStowageColumnEnum.StowedVolume:
            if (!activeStowagePlan) return 0; // TODO: LOGIC-ERROR - This case uses stowedWeightInTanks() instead of calculating actual volume. Should use calculateVolume() with stowedWeightInTanks() result to get proper volume comparison.
            return compareDecimals(stowedWeightInTanks(activeStowagePlan, a), stowedWeightInTanks(activeStowagePlan, b), isOperationListSortDesc);
        case SortStowageColumnEnum.TradeName:
            return compare(a.cargo?.product?.tradeName, b.cargo?.product?.tradeName, isOperationListSortDesc);
        case SortStowageColumnEnum.ProductName:
            return compare(a.cargo?.product?.category?.name, b.cargo?.product?.category?.name, isOperationListSortDesc);
        case SortStowageColumnEnum.OperationType:
            return compare(a.operationType, b.operationType, isOperationListSortDesc);
        case SortStowageColumnEnum.PortNames:
            return comparePorts(a.cargo ?? null, b.cargo ?? null, isOperationListSortDesc);
        default:
            return 0;
    }
}
